import { useEffect, useState } from "react";
import { useController, useFormContext } from "react-hook-form";
import { ArrowRightAltIcon, ClearBadge, Color } from "@bonliva-crm/ui";
import {
  CustomerSearchDataFragment,
  useSearchCustomersLazyQuery
} from "@bonliva-crm/graphql";
import { Box, Chip, IconButton, makeStyles, Typography } from "@material-ui/core";
import { CustomerFullSearchModal } from "@customers-new/customer-search/full/customer-full-search-modal";
import { FilterWithFavouriteProps } from "../utils/filters-record-types";
import { NonCollapsibleFavFilter } from "./_base/non-collapsible-fav-filter";
import { useLocale } from "@hooks";

export type CustomersNameFilterProps = FilterWithFavouriteProps;

export const CustomersNameFilter = (props: CustomersNameFilterProps) => {
  const [modalOpen, setModalOpen] = useState(false);
  const [searchCustomersQuery] = useSearchCustomersLazyQuery();
  const [value, setValue] = useState<CustomerSearchDataFragment[]>([]);
  const { locale } = useLocale();
  const classes = useStyles();
  const title = props.label ?? locale.Customers;

  const { resetField, setValue: setFormValue } = useFormContext();
  const {
    field,
    fieldState: { isDirty }
  } = useController({ name: props.name });

  useEffect(() => {
    if (!field?.value?.length) {
      setValue([]);
      return;
    }

    searchCustomersQuery({
      variables: {
        filter: { includeCustomerIds: field?.value?.map((v) => v.value) },
        limit: field?.value?.length || 1,
        skip: 0
      },
      fetchPolicy: "cache-first"
    }).then((res) => {
      setValue(res.data?.customers?.items || []);
    });
  }, [field?.value, searchCustomersQuery]);

  const removeItem = (id: string) => {
    setFormValue(
      props.name,
      field.value.filter((v) => v.value !== id),
      { shouldDirty: true }
    );
  };

  const modal = (
    <CustomerFullSearchModal
      open={modalOpen}
      setOpen={setModalOpen}
      isMulti
      onChange={(value) => {
        setFormValue(
          props.name,
          value.map((v) => ({ label: v.Name, value: v._id })),
          { shouldDirty: true }
        );
      }}
      value={value}
    />
  );

  if (props.setFavorite) {
    return (
      <>
        <NonCollapsibleFavFilter
          favorite={props.favorite}
          onSetFavorite={props.setFavorite}
          title={title}
          isDirty={isDirty}
          isClearable={isDirty}
          onClear={(e) => {
            resetField(props.name);
            e.stopPropagation();
            e.preventDefault();
          }}
          onTitleClick={() => setModalOpen(true)}
          action={
            <IconButton onClick={() => setModalOpen(true)}>
              <ArrowRightAltIcon />
            </IconButton>
          }
        >
          {value?.length > 0 && (
            <Box display="flex" flexWrap="wrap" flex="1" gridGap="8px">
              {value.map((v) => (
                <Chip
                  size="small"
                  key={v._id}
                  label={v.Name}
                  onDelete={() => removeItem(v._id)}
                />
              ))}
            </Box>
          )}
        </NonCollapsibleFavFilter>

        {modal}
      </>
    );
  }

  return (
    <>
      <ClearBadge
        invisible={!field?.value?.length}
        onClear={() => {
          resetField(props.name);
        }}
      >
        <div className={classes.favouriteButton} onClick={() => setModalOpen(true)}>
          <Typography variant="body1" style={{ color: Color.DetailsInput }}>
            {title}
          </Typography>

          <ArrowRightAltIcon fontSize="small" style={{ marginLeft: 8 }} />

          {field?.value?.length > 0 && (
            <Box
              display="flex"
              flexWrap="wrap"
              flex="1"
              gridGap="8px"
              ml={1}
              alignItems={"center"}
            >
              {field?.value?.map((el, i) => {
                if (i === 4) {
                  return "...";
                }
                if (i > 4) {
                  return null;
                }
                return (
                  <Chip
                    key={el._id}
                    size="small"
                    label={el.label}
                    onDelete={() => {
                      const newValue = [...(field?.value ? field.value : [])];
                      newValue.splice(i, 1);
                      setFormValue(props.name, newValue, { shouldDirty: true });
                    }}
                  />
                );
              })}
            </Box>
          )}
        </div>
      </ClearBadge>

      {modal}
    </>
  );
};

const useStyles = makeStyles({
  favouriteButton: {
    border: `1px solid ${Color.LightGray}`,
    borderRadius: "4px",
    padding: "8px",
    cursor: "pointer",
    whiteSpace: "nowrap",
    display: "flex",
    alignItems: "center",

    "&:hover": {
      backgroundColor: Color.LightGray
    }
  }
});
